export const QUOTE_LIST_STORAGE_KEY = "metal-asia:quote-list:v1";
export const QUOTE_LIST_CHANGE_EVENT = "metal-asia:quote-list-change";
export const MAX_QUOTE_LIST_ITEMS = 8;

const EMPTY_QUOTE_LIST_SNAPSHOT = "[]";
let memorySnapshot = EMPTY_QUOTE_LIST_SNAPSHOT;
const PRODUCT_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function normalizeQuoteList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  const unique = new Set<string>();
  for (const candidate of value) {
    if (
      typeof candidate !== "string" ||
      candidate.length > 120 ||
      !PRODUCT_SLUG_PATTERN.test(candidate)
    ) {
      continue;
    }
    unique.add(candidate);
    if (unique.size >= MAX_QUOTE_LIST_ITEMS) break;
  }
  return [...unique];
}

export function parseQuoteListSnapshot(snapshot: string | null | undefined) {
  if (!snapshot) return [];
  try {
    return normalizeQuoteList(JSON.parse(snapshot));
  } catch {
    return [];
  }
}

export function quoteListFromSearch(search: string) {
  const parameters = new URLSearchParams(search);
  return normalizeQuoteList(
    (parameters.get("products") ?? parameters.get("quote") ?? "")
      .split(",")
      .filter(Boolean),
  );
}

export function getQuoteListSnapshot() {
  if (typeof window === "undefined") return EMPTY_QUOTE_LIST_SNAPSHOT;
  try {
    memorySnapshot = window.localStorage.getItem(QUOTE_LIST_STORAGE_KEY) ?? EMPTY_QUOTE_LIST_SNAPSHOT;
    return memorySnapshot;
  } catch {
    return memorySnapshot;
  }
}

export function getQuoteListServerSnapshot() {
  return EMPTY_QUOTE_LIST_SNAPSHOT;
}

export function subscribeToQuoteList(callback: () => void) {
  if (typeof window === "undefined") return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (event.key === QUOTE_LIST_STORAGE_KEY || event.key === null) callback();
  };
  window.addEventListener("storage", handleStorage);
  window.addEventListener(QUOTE_LIST_CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(QUOTE_LIST_CHANGE_EVENT, callback);
  };
}

export function writeQuoteList(slugs: readonly string[]) {
  if (typeof window === "undefined") return [];
  const normalized = normalizeQuoteList(slugs);
  memorySnapshot = JSON.stringify(normalized);
  try {
    window.localStorage.setItem(
      QUOTE_LIST_STORAGE_KEY,
      memorySnapshot,
    );
  } catch {
    // The in-page state still updates through the custom event when storage is unavailable.
  }
  window.dispatchEvent(new Event(QUOTE_LIST_CHANGE_EVENT));
  return normalized;
}

export function buildQuoteListSearch(slugs: readonly string[]) {
  const normalized = normalizeQuoteList(slugs);
  const parameters = new URLSearchParams();
  if (normalized.length) parameters.set("products", normalized.join(","));
  return parameters.toString();
}
